import _isEqual from "lodash/isEqual";
import { useCallback, useEffect, useRef, useState } from 'react';
import { reaction } from 'mobx';
const MAX_HISTORY_ENTRIES = 50;
export function useQueryHistory({
  panel,
  summarize,
  generateTitle,
  onAutoTitle
}) {
  // Ensure the panel model always has a history array (prevents null in PUT requests)
  if (!Array.isArray(panel.get('history'))) {
    panel.set({
      history: []
    });
  }
  const [entries, setEntries] = useState(() => panel.get('history') ?? []);
  const [headIndex, setHeadIndex] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const entriesRef = useRef(entries);
  entriesRef.current = entries;
  const headIndexRef = useRef(headIndex);
  headIndexRef.current = headIndex;

  // When restoring from history, skip recording the config change as a new entry
  const isRestoringRef = useRef(false);

  // Track the last auto-generated title so we can keep updating it on subsequent queries
  const lastAutoTitleRef = useRef('');
  const toggleOpen = useCallback(() => setIsOpen(prev => !prev), []);

  /** Sync entries to state, ref, and panel atomically */
  const commitEntries = useCallback(updated => {
    setEntries(updated);
    entriesRef.current = updated;
    panel.set({
      history: updated
    });
  }, [panel]);

  /** Sync headIndex to state and ref atomically */
  const commitHead = useCallback(index => {
    setHeadIndex(index);
    headIndexRef.current = index;
  }, []);

  /** Patch a single history entry matched by timestamp */
  const patchEntry = useCallback((timestamp, patch) => {
    const latest = entriesRef.current;
    const idx = latest.findIndex(e => e.timestamp === timestamp);
    if (idx >= 0) {
      const patched = [...latest];
      patched[idx] = {
        ...patched[idx],
        ...patch
      };
      commitEntries(patched);
    }
  }, [commitEntries]);

  /** Begin a history restore: suppress new recordings and queue flag reset */
  const beginRestore = useCallback(() => {
    isRestoringRef.current = true;
    lastAutoTitleRef.current = panel.get('title') || '';
    queueMicrotask(() => {
      isRestoringRef.current = false;
    });
  }, [panel]);

  // MobX reaction: watch for panel config changes and auto-record history entries.
  // Since autoSave is debounced (300ms), this reaction fires as a microtask well
  // before the save — ensuring history is included in the save payload.
  useEffect(() => {
    let prevData = panel.get('config')?.data;
    let prevChart = panel.get('config')?.chart;
    let prevConfig = panel.get('config');
    const disposer = reaction(() => panel.get('config'), config => {
      const curr = config?.data;
      const prev = prevData;
      const currChart = config?.chart;
      const prevChartSnap = prevChart;
      const prevConfigSnap = prevConfig;
      prevData = curr;
      prevChart = currChart;
      prevConfig = config;

      // Skip if restoring from history (flag is cleared via queueMicrotask in beginRestore)
      if (isRestoringRef.current) {
        return;
      }

      // Skip if current query has no metrics, or the entire config is identical
      if (!curr?.metrics?.length || _isEqual(prevConfigSnap, config)) {
        return;
      }

      // If viewing an old entry, discard forward entries before recording
      const base = headIndexRef.current > 0 ? entriesRef.current.slice(headIndexRef.current) : entriesRef.current;
      const newEntry = {
        timestamp: new Date().toISOString(),
        user_id: '',
        summary: 'Summarizing…',
        query: curr,
        chart: currChart,
        autoTitle: '',
        user_prompt: ''
      };
      commitEntries([newEntry, ...base].slice(0, MAX_HISTORY_ENTRIES));
      commitHead(0);

      // Async: generate summary — skip for the first entry (no meaningful diff)
      const summaryPromise = prev ? summarize({
        data: prev,
        chart: prevChartSnap
      }, {
        data: curr,
        chart: currChart
      }) : Promise.resolve('Initial query');
      summaryPromise.then(summary => patchEntry(newEntry.timestamp, {
        summary
      })).catch(() => patchEntry(newEntry.timestamp, {
        summary: 'Manual change'
      }));

      // Async: generate auto-title
      generateTitle(curr).then(autoTitle => {
        patchEntry(newEntry.timestamp, {
          autoTitle
        });
        // Auto-title the panel if user hasn't manually set one,
        // or if the current title is still the previous auto-title (i.e. not manually edited)
        const currentTitle = panel.get('title') || '';
        if (autoTitle && (!currentTitle || currentTitle === lastAutoTitleRef.current)) {
          lastAutoTitleRef.current = autoTitle;
          panel.set({
            title: autoTitle
          });
          onAutoTitle?.(autoTitle);
        }
      }).catch(() => undefined);
    }, {
      fireImmediately: false
    });
    return disposer;
  }, [panel, summarize, generateTitle, onAutoTitle, commitEntries, commitHead, patchEntry]);
  const goToEntry = useCallback(index => {
    if (index < 0 || index >= entriesRef.current.length) {
      return null;
    }
    commitHead(index);
    beginRestore();
    return entriesRef.current[index].query;
  }, [commitHead, beginRestore]);
  const removeEntry = useCallback(index => {
    const {
      current
    } = entriesRef;
    if (index < 0 || index >= current.length || index === headIndexRef.current) {
      return false;
    }
    const updated = [...current];
    updated.splice(index, 1);
    // Adjust headIndex if a preceding entry was removed
    const newHeadIndex = index < headIndexRef.current ? headIndexRef.current - 1 : headIndexRef.current;
    commitEntries(updated);
    commitHead(newHeadIndex);
    return true;
  }, [commitEntries, commitHead]);
  const setToHead = useCallback(index => {
    const {
      current
    } = entriesRef;
    if (index <= 0 || index >= current.length) {
      return null;
    }
    // Discard entries at indices 0..(index-1)
    const updated = current.slice(index);
    commitEntries(updated);
    commitHead(0);
    beginRestore();
    return updated[0]?.query ?? null;
  }, [commitEntries, commitHead, beginRestore]);
  return {
    entries,
    headIndex,
    isViewingHistory: headIndex > 0,
    goToEntry,
    removeEntry,
    setToHead,
    isOpen,
    toggleOpen
  };
}